import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus, Star, Trash2 } from 'lucide-react';

import type { WorkflowSummary } from '@tasks/contracts';

import {
  bindIssueType,
  createWorkflow,
  deleteWorkflow,
  renameWorkflow,
  setDefaultWorkflow,
} from '../../api/workflow';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { ISSUE_TYPE_ORDER, issueTypeLabel } from '../../i18n/enums';
import { humanizeError } from '../../lib/errors';
import type { IssueType } from '../../lib/types';
import styles from './WorkflowSettingsPage.module.css';

export type WorkflowSelectorProps = {
  readonly projectKey: string;
  readonly workflows: readonly WorkflowSummary[];
  readonly selectedId: string | null;
  readonly isLoading: boolean;
  readonly onSelect: (workflowId: string) => void;
  readonly onChanged: () => void;
};

/**
 * Панель workflow проекта: выбор набора, создание, переименование, удаление и
 * привязка типов задач (docs/03 §1).
 *
 * Список приходит сверху: страница уже загрузила его и знает выбранный набор.
 * Смена привязки типа — единственная операция, которая двигает существующие
 * задачи, поэтому она подтверждается числом затронутых задач.
 */
export function WorkflowSelector({
  projectKey,
  workflows,
  selectedId,
  isLoading,
  onSelect,
  onChanged,
}: WorkflowSelectorProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [isCreateOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [copyFrom, setCopyFrom] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<WorkflowSummary | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<WorkflowSummary | null>(null);
  const [bindTarget, setBindTarget] = useState<{
    readonly type: IssueType;
    readonly workflow: WorkflowSummary;
    readonly count: number;
  } | null>(null);

  const reportError = (cause: unknown) => {
    const human = humanizeError(cause);
    toast.error(human.message, { code: human.code });
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.workflows(projectKey) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.workflow(projectKey) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(projectKey) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(projectKey) });
    onChanged();
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createWorkflow(projectKey, {
        name: newName.trim(),
        ...(copyFrom === null ? {} : { copyFromWorkflowId: copyFrom }),
      }),
    onSuccess: (created) => {
      setCreateOpen(false);
      setNewName('');
      setCopyFrom(null);
      invalidate();
      onSelect(created.id);
      toast.success(`Workflow «${created.name}» создан`);
    },
    onError: reportError,
  });

  const renameMutation = useMutation({
    mutationFn: (workflow: WorkflowSummary) =>
      renameWorkflow(projectKey, workflow.id, { name: renameValue.trim() }),
    onSuccess: () => {
      setRenameTarget(null);
      setRenameValue('');
      invalidate();
      toast.success('Workflow переименован');
    },
    onError: reportError,
  });

  const defaultMutation = useMutation({
    mutationFn: (workflow: WorkflowSummary) => setDefaultWorkflow(projectKey, workflow.id),
    onSuccess: (workflow) => {
      invalidate();
      toast.success(`«${workflow.name}» — workflow по умолчанию`);
    },
    onError: reportError,
  });

  const deleteMutation = useMutation({
    mutationFn: (workflow: WorkflowSummary) => deleteWorkflow(projectKey, workflow.id),
    onSuccess: (result, workflow) => {
      setDeleteTarget(null);
      invalidate();
      if (workflow.id === selectedId) {
        const fallback = workflows.find((item) => item.isDefault && item.id !== workflow.id);
        if (fallback) onSelect(fallback.id);
      }
      toast.success(
        result.movedIssues === 0
          ? 'Workflow удалён'
          : `Workflow удалён, задачи перенесены: ${result.movedIssues}`,
      );
    },
    onError: reportError,
  });

  const bindMutation = useMutation({
    mutationFn: (variables: { readonly type: IssueType; readonly workflowId: string }) =>
      bindIssueType(projectKey, variables.type, variables.workflowId),
    onSuccess: (result, variables) => {
      setBindTarget(null);
      invalidate();
      toast.success(
        result.movedIssues === 0
          ? `Тип «${issueTypeLabel(variables.type)}» переведён в новый workflow`
          : `Тип «${issueTypeLabel(variables.type)}» переведён, задач перенесено: ${result.movedIssues}`,
      );
    },
    onError: reportError,
  });

  const selected = workflows.find((workflow) => workflow.id === selectedId) ?? null;

  /** Сколько задач типа сейчас в статусах workflow — это и есть будущий переезд. */
  const countOf = (workflow: WorkflowSummary, type: IssueType): number =>
    workflow.issueTypeCounts.find((item) => item.issueType === type)?.value ?? 0;

  return (
    <section className={styles['block']} aria-label="Workflow проекта">
      <div className={styles['workflowHeader']}>
        <h2>Workflow</h2>
        <div className={styles['cellActions']}>
          <Button variant="secondary" icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            Новый workflow
          </Button>
        </div>
      </div>
      <p className={styles['hint']}>
        Каждый тип задачи ведёт один workflow. Пока привязку не меняли, все типы
        идут по набору по умолчанию. Задача попадает в начальный статус своего
        набора.
      </p>

      {isLoading ? (
        <Skeleton variant="row" count={2} />
      ) : (
        <ul className={styles['workflowList']}>
          {workflows.map((workflow) => {
            const isSelected = workflow.id === selectedId;
            return (
              <li
                key={workflow.id}
                className={isSelected ? styles['workflowItemActive'] : styles['workflowItem']}
              >
                <button
                  type="button"
                  className={styles['workflowPick']}
                  aria-pressed={isSelected}
                  onClick={() => onSelect(workflow.id)}
                >
                  <span className={styles['workflowName']}>{workflow.name}</span>
                  {workflow.isDefault ? (
                    <span className={styles['badge']}>по умолчанию</span>
                  ) : null}
                  <span className={styles['badge']}>
                    типов: {workflow.issueTypes.length}
                  </span>
                </button>
                <span className={styles['workflowActions']}>
                  {workflow.isDefault ? null : (
                    <Button
                      variant="ghost"
                      icon={<Star size={14} />}
                      loading={defaultMutation.isPending}
                      aria-label="Сделать workflow по умолчанию"
                      onClick={() => defaultMutation.mutate(workflow)}
                    >
                      По умолчанию
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    icon={<Copy size={14} />}
                    aria-label="Переименовать"
                    onClick={() => {
                      setRenameTarget(workflow);
                      setRenameValue(workflow.name);
                    }}
                  >
                    Имя
                  </Button>
                  <Button
                    variant="ghost"
                    icon={<Trash2 size={14} />}
                    aria-label="Удалить workflow"
                    disabled={workflow.isDefault || workflows.length === 1}
                    onClick={() => setDeleteTarget(workflow)}
                  >
                    Удалить
                  </Button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {selected ? (
        <div className={styles['bindingBlock']}>
          <h3>Типы задач</h3>
          <p className={styles['hint']}>
            Смена типа переносит его задачи в начальный статус нового workflow.
            Число в скобках — сколько задач переедет.
          </p>
          <table className={styles['statusTable']}>
            <thead>
              <tr>
                <th scope="col">Тип задачи</th>
                <th scope="col">Workflow</th>
                <th scope="col">Задач в этом наборе</th>
              </tr>
            </thead>
            <tbody>
              {ISSUE_TYPE_ORDER.map((type) => {
                const owner =
                  workflows.find((workflow) => workflow.issueTypes.includes(type)) ?? null;
                return (
                  <tr key={type}>
                    <td>{issueTypeLabel(type)}</td>
                    <td>{owner?.name ?? '—'}</td>
                    <td>{owner === null ? 0 : countOf(owner, type)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className={styles['bindingControls']}>
            {ISSUE_TYPE_ORDER.map((type) => {
              const owner =
                workflows.find((workflow) => workflow.issueTypes.includes(type)) ?? null;
              if (owner?.id === selected.id) return null;
              const affected = owner === null ? 0 : countOf(owner, type);
              return (
                <Button
                  key={type}
                  variant="secondary"
                  disabled={bindMutation.isPending}
                  onClick={() => setBindTarget({ type, workflow: selected, count: affected })}
                >
                  {`«${issueTypeLabel(type)}» → ${selected.name}`}
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}

      <Modal
        open={isCreateOpen}
        title="Новый workflow"
        onClose={() => setCreateOpen(false)}
        footer={
          <div className={styles['modalFooter']}>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Отмена
            </Button>
            <Button
              loading={createMutation.isPending}
              disabled={newName.trim().length === 0}
              onClick={() => createMutation.mutate()}
            >
              Создать
            </Button>
          </div>
        }
      >
        <Input
          label="Название"
          value={newName}
          placeholder="Например, Разработка"
          onChange={(event) => setNewName(event.target.value)}
        />
        <p className={styles['hint']}>
          Новый набор получает базовые статусы и переходы. Можно скопировать
          статусы и переходы существующего workflow — например, «как основной,
          но с ревью».
        </p>
        <label className={styles['copyRow']}>
          <span>Копировать из</span>
          <select
            className={styles['select']}
            value={copyFrom ?? ''}
            onChange={(event) => setCopyFrom(event.target.value === '' ? null : event.target.value)}
          >
            <option value="">Не копировать</option>
            {workflows.map((workflow) => (
              <option key={workflow.id} value={workflow.id}>
                {workflow.name}
              </option>
            ))}
          </select>
        </label>
      </Modal>

      <Modal
        open={renameTarget !== null}
        title={`Переименовать «${renameTarget?.name ?? ''}»`}
        onClose={() => setRenameTarget(null)}
        footer={
          <div className={styles['modalFooter']}>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>
              Отмена
            </Button>
            <Button
              loading={renameMutation.isPending}
              disabled={renameValue.trim().length === 0}
              onClick={() => {
                if (renameTarget) renameMutation.mutate(renameTarget);
              }}
            >
              Сохранить
            </Button>
          </div>
        }
      >
        <Input
          label="Название"
          value={renameValue}
          onChange={(event) => setRenameValue(event.target.value)}
        />
      </Modal>

      <ConfirmDialog
        open={bindTarget !== null}
        title={`Перевести тип «${bindTarget === null ? '' : issueTypeLabel(bindTarget.type)}»?`}
        description={
          bindTarget === null
            ? ''
            : bindTarget.count === 0
              ? `Задач этого типа в прежнем наборе нет. Новые задачи пойдут по «${bindTarget.workflow.name}».`
              : `Задачи этого типа (${bindTarget.count}) переедут в начальные статусы «${bindTarget.workflow.name}», и в истории каждой появится запись о смене статуса.`
        }
        confirmLabel="Перевести"
        isPending={bindMutation.isPending}
        onConfirm={() => {
          if (bindTarget) {
            bindMutation.mutate({ type: bindTarget.type, workflowId: bindTarget.workflow.id });
          }
        }}
        onClose={() => setBindTarget(null)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Удалить workflow «${deleteTarget?.name ?? ''}»?`}
        description="Статусы и переходы набора будут удалены, а его задачи переедут в начальные статусы workflow по умолчанию."
        confirmLabel="Удалить"
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget);
        }}
        onClose={() => setDeleteTarget(null)}
      />
    </section>
  );
}
